/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  BlockUI,
  ContentType,
  convertKBToMB,
  DEFAULT_TIMEZONE,
  DocumentViewer,
  FileType,
  getDiagnosticReportOptions,
  getReportContentByType,
  IDiagnosticReportContent,
  LIMIT_REPORT_MB_SIZE,
  OptionTypeBase,
  CustomSwitch as ReportCustomSwitch,
  FARO_EVENTS,
  faroLogger,
} from "@advapacs/common";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "@src/config/store";
import {
  getStudyDiagnosticReport,
  studyDownloadPdf,
  studyFetchReportDataContent,
} from "./ducks/operators";
import { IRootState } from "@src/config/reducers";
import { useSelector } from "react-redux";
import ZoomOutIcon from "@src/assets/icons/zoomout-icon.svg";
import CloseWhiteIcon from "@src/assets/icons/close-white.svg";
import DownloadIcon from "@src/assets/icons/download-white.svg";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { NoResult } from "@src/components/NoResult";
import { isMobileOnly } from "react-device-detect";
import { getMobileOperatingSystem } from "@src/utils/utils";
import axios from "axios";
import { studyOrderAPI } from "@src/config/api";

interface IReportViewer {
  viewModeId?: string;
  hasReport?: boolean;
  isMobile?: boolean;
  isLoadData?: boolean;
}

export interface IReportContent {
  [key: string]: string;
}

export const ReportViewer: FC<IReportViewer> = ({
  viewModeId,
  isMobile,
  isLoadData,
}) => {
  const dispatch = useAppDispatch();
  const fullScreenRef = useRef(null);
  const [contents, setContents] = useState<IReportContent>({});
  const handle = useFullScreenHandle();
  const [loading, setLoading] = useState(false);
  const [hasGenerateReport, setHasGenerateReport] = useState(false);
  const [contentOptions, setContentOptions] = useState<OptionTypeBase[]>([]);
  const [selectedReportContent, setSelectedReportContent] =
    useState<IDiagnosticReportContent>();
  const { reportDetail } = useSelector(
    (state: IRootState) => state.examsReducer
  );

  useEffect(() => {
    // just load data when over delay time and hasReport.
    if (viewModeId && isLoadData) {
      dispatch(getStudyDiagnosticReport(viewModeId));
    }
  }, [dispatch, viewModeId, isLoadData]);

  useEffect(() => {
    if (reportDetail) {
      const report = getDiagnosticReportOptions(reportDetail);
      setContentOptions(report.options);
      setHasGenerateReport(report.isGeneratePdfReport);
      let defaultValue = report.options.filter(
        (c) => c.label === FileType.PDF
      )[0];
      if (!defaultValue) defaultValue = report.options[0];
      setSelectedReportContent(defaultValue?.value);
    }
  }, [reportDetail]);

  const loadStudyDefaultContent = async (
    id: string,
    reportContent: IDiagnosticReportContent,
    report: boolean
  ) => {
    try {
      setLoading(true);
      const result = await studyFetchReportDataContent(
        id,
        reportContent,
        report
      );
      const newContents = {
        ...contents,
        // @ts-ignore
        [reportContent.tag]: result,
      };
      setContents(newContents);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      selectedReportContent &&
      selectedReportContent.id &&
      // @ts-ignore
      contents[selectedReportContent.tag] === undefined
    ) {
      const fileConverted = convertKBToMB(
        selectedReportContent?.size as number
      );
      const isFileLarge = fileConverted > LIMIT_REPORT_MB_SIZE;
      const isDPFFile =
        selectedReportContent?.tag?.toLowerCase() === ContentType.PDF_TAG;
      const shouldDontCallAPI = isFileLarge && isDPFFile;
      if (shouldDontCallAPI) return;
      loadStudyDefaultContent(
        viewModeId as string,
        selectedReportContent,
        hasGenerateReport
      ).then(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    contents,
    selectedReportContent,
    viewModeId,
    hasGenerateReport,
  ]);

  /**
   * Memo
   */
  const isShowHTMLOrTextPlain = useMemo(() => {
    return (
      selectedReportContent &&
      selectedReportContent?.contentType !== ContentType.APPLICATION_PDF
    );
  }, [selectedReportContent]);

  const reportPDFSize = useMemo(
    () => convertKBToMB(selectedReportContent?.size as number),
    [selectedReportContent]
  );

  const isShowPDF = useMemo(() => {
    return (
      selectedReportContent &&
      selectedReportContent?.contentType === ContentType.APPLICATION_PDF &&
      // @ts-ignore
      (contents[selectedReportContent.tag] !== undefined ||
        reportPDFSize > LIMIT_REPORT_MB_SIZE)
    );
  }, [selectedReportContent, contents, reportPDFSize]);

  const isShowDownloadButton = useMemo(() => {
    return contentOptions?.filter((x) => x.label === FileType.PDF).length > 0;
  }, [contentOptions]);

  const handleDownload = () => {
    faroLogger.event(FARO_EVENTS.REPORT_DOWNLOAD, { entry_point: "detail" });
    studyDownloadPdf(
      viewModeId as string,
      contentOptions?.find((x) => x.label === FileType.PDF)?.value?.id,
      hasGenerateReport
    );
  };

  const dynamicHeightCal = useMemo(() => {
    const device = getMobileOperatingSystem();
    let extend = 0;

    if (device === "IOS") extend = 70;
    if (device === "AND") extend = 60;
    if (isMobileOnly) return 240 + extend;
    if (isMobile) return 245 + extend;
    return 385;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isMobileOnly]);

  const isUnsupportedPDFViewer = useMemo(() => {
    if (isMobile && selectedReportContent?.tag === ContentType.PDF_TAG) {
      return !navigator.pdfViewerEnabled;
    }
    return false;
  }, [isMobile, selectedReportContent?.tag]);

  // iOS Safari (iPhone and iPad) doesn't reliably support the Fullscreen API
  // for arbitrary elements, so hide the toggle there; for every other device
  // fall back to feature-detecting the Fullscreen API itself.
  const isFullScreenSupported = useMemo(() => {
    if (getMobileOperatingSystem() === "IOS") return false;
    if (typeof document === "undefined") return false;
    return !!(
      document.fullscreenEnabled ||
      // @ts-ignore
      document.webkitFullscreenEnabled ||
      // @ts-ignore
      document.mozFullScreenEnabled ||
      // @ts-ignore
      document.msFullscreenEnabled
    );
  }, []);

  const pdfURL = useMemo(() => {
    const condition =
      !reportDetail?.generatePdf &&
      selectedReportContent?.tag === ContentType.PDF_TAG;
    const params: Record<string, string> = {
      origin: window.origin,
      "x-tz": DEFAULT_TIMEZONE.value,
    };
    const urlParams = new URLSearchParams(params);
    const path = condition
      ? studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT_CONTENT(
          viewModeId as string,
          selectedReportContent?.id
        )
      : studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT_PDF(
          viewModeId as string,
          selectedReportContent?.id as string
        );
    const url = `${axios.defaults.baseURL}${path}`;
    return condition ? url : `${url}?${urlParams.toString()}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReportContent?.id, viewModeId, reportDetail]);

  const openNewTab = async () => {
    try {
      const response = await axios.get(pdfURL, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {}
  };

  return (
    <>
      <FullScreen handle={handle}>
        <>
          <div
            ref={fullScreenRef}
            className={`report-viewer-wrapper ${
              isUnsupportedPDFViewer ? "" : "d-flex flex-direction-column"
            }`}
            style={{
              height: isUnsupportedPDFViewer
                ? "auto"
                : `calc(100vh - ${dynamicHeightCal}px)`,
              overflow: "hidden",
            }}
          >
            <div className="title d-flex justify-content-between">
              <div
                className={`flex-1 d-flex justify-content-between align-items-center`}
              >
                <h5 className="font-weight m-r-xxs">Report</h5>
                {contentOptions && contentOptions?.length > 1 && (
                  <>
                    <ReportCustomSwitch
                      className={handle.active ? "dark" : ""}
                      value={
                        contentOptions?.filter(
                          (c) => c.value === selectedReportContent
                        )[0]
                      }
                      options={contentOptions as OptionTypeBase[]}
                      onClick={(vl) => {
                        setSelectedReportContent(
                          vl as unknown as IDiagnosticReportContent
                        );
                      }}
                    />
                  </>
                )}
                {contentOptions?.length > 0 && isFullScreenSupported && (
                  <div
                    className="d-flex flex-center cursor-pointer "
                    onClick={() => {
                      if (handle.active) {
                        handle.exit();
                      } else {
                        handle.enter();
                      }
                    }}
                  >
                    {!handle.active && !isMobileOnly && (
                      <span className="text-primary">Zoom In</span>
                    )}
                    <img
                      src={handle.active ? CloseWhiteIcon : ZoomOutIcon}
                      className="p-xs"
                      alt="Zoom out"
                    />
                  </div>
                )}
              </div>
            </div>
            {contentOptions?.length > 0 && (
              <div
                className={`${
                  !handle.active ? "m-t-sm" : ""
                } h-100 min-h-20 overflow-auto`}
              >
                <BlockUI isBlocked={loading}>
                  <>
                    {isShowHTMLOrTextPlain && (
                      <DocumentViewer
                        type="HTML"
                        srcDoc={getReportContentByType(
                          // @ts-ignore
                          contents[selectedReportContent.tag],
                          // @ts-ignore
                          selectedReportContent?.contentType
                        )}
                      />
                    )}

                    {isShowPDF && (
                      <DocumentViewer
                        isDark={handle.active}
                        isMobile={isMobile}
                        isFullHeight
                        isLargestFile={reportPDFSize > LIMIT_REPORT_MB_SIZE}
                        type="PDF"
                        openNewTab={() => {
                          if (
                            !reportDetail?.generatePdf &&
                            selectedReportContent?.tag === ContentType.PDF_TAG
                          ) {
                            openNewTab();
                          } else {
                            window.open(pdfURL, "_blank");
                          }
                        }}
                        // @ts-ignore
                        src={contents[selectedReportContent.tag]}
                      />
                    )}
                  </>
                </BlockUI>
              </div>
            )}
            {(!contentOptions || contentOptions?.length === 0) && (
              <NoResult type="Report" />
            )}
          </div>
          {isShowDownloadButton && (
            <div
              className={`d-flex flex-end report-footer ${
                !handle.active ? "m-t-xs" : "m-t-xxs"
              } ${isUnsupportedPDFViewer ? "unsupport" : ""}`}
            >
              <button
                className={`btn btn-primary ${isMobile ? "w-100" : ""}`}
                onClick={handleDownload}
              >
                <img
                  className="m-r-xxs padding-b-3px"
                  src={DownloadIcon}
                  alt="Download Icon"
                />
                <span>Download</span>
              </button>
            </div>
          )}
        </>
      </FullScreen>
    </>
  );
};
